import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';

import BaseViewsContext from 'contexts/BaseViewsContext';
import BoardContext from 'contexts/BoardContext';
import PerformanceBoardSettingsContext from '../../contexts/PerformanceBoardSettingsContext';
import BoardGeneralSettings from './BoardGeneralSettings';
import useEntityField from 'components/TargetManager/hooks/useEntityField';
import useUpdateGeneral from '../hooks/useUpdateGeneral';
import isPerformanceBoard from 'isPerformanceBoard';

const BoardGeneralSettingsContainer = ({
  board,
}: {
  board: PerformanceBoardTypes.Board;
}) => {
  const [name, setName] = useState<string>(board.name);
  const entityField = useEntityField(board.dataType);
  const { hasUnsavedChanges, setHasUnsavedChanges } = useContext(
    PerformanceBoardSettingsContext,
  );
  const [defaultSort, setDefaultSort] = useState<{
    direction: 'asc' | 'desc';
    field: string;
  }>(
    board.defaultSort
      ? board.defaultSort
      : {
          field: entityField || '',
          direction: 'asc',
        },
  );
  const [plates, setPlates] = useState<FilterPlateType[]>(() => board.plates);
  const [isEntityFilterEnabled, setIsEntityFilterEnabled] = useState<boolean>(
    () => {
      if (board) {
        return !!board.isEntityFilterEnabled;
      }
      return false;
    },
  );
  const [isAsset, setIsAsset] = useState<boolean>(
    board ? !!board.isAsset : false,
  );
  const [labelIds, setLabelIds] = useState<string[]>(board.labelIds);
  // Seeded from the raw board value (may be undefined) so that boards which have
  // never opted in stay byte-for-byte unchanged on save — the fields are only
  // written once a staff member actually picks a value.
  const [slideoutWidth, setSlideoutWidth] = useState<
    'default' | 'wide' | undefined
  >(board.slideoutWidth);
  const [slideoutDisplayMode, setSlideoutDisplayMode] = useState<
    'squish' | 'overlay' | undefined
  >(board.slideoutDisplayMode);

  const draftBoard = useMemo(() => {
    const next: PerformanceBoardTypes.Board = {
      ...board,
      name,
      defaultSort,
      plates,
      isEntityFilterEnabled,
      isAsset,
      labelIds,
    };
    if (slideoutWidth !== undefined) {
      next.slideoutWidth = slideoutWidth;
    }
    if (slideoutDisplayMode !== undefined) {
      next.slideoutDisplayMode = slideoutDisplayMode;
    }
    return next;
  }, [
    board,
    defaultSort,
    isAsset,
    isEntityFilterEnabled,
    labelIds,
    name,
    plates,
    slideoutWidth,
    slideoutDisplayMode,
  ]);

  useEffect(() => {
    if (!_.isEqual(draftBoard, board)) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [
    board,
    board.defaultSort,
    board.isAsset,
    board.isEntityFilterEnabled,
    board.labelIds,
    board.name,
    board.plates,
    defaultSort,
    draftBoard,
    isAsset,
    isEntityFilterEnabled,
    labelIds,
    plates,
    setHasUnsavedChanges,
    name,
    slideoutWidth,
    slideoutDisplayMode,
  ]);

  const { baseViews } = useContext(BaseViewsContext);
  const baseView = baseViews[board.dataType];
  const updateGeneral = useUpdateGeneral();

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setName(event.target.value);
  };

  const fieldOptions =
    baseView && baseView.fieldOrder
      ? baseView.fieldOrder.map((field) => ({
          label: field,
          value: field,
          isSelected: defaultSort.field === field,
          onSelected: () => {
            setDefaultSort({ ...defaultSort, field });
          },
        }))
      : undefined;

  const getSortCode = ({ sort }: { sort: string }) => {
    return sort === 'Ascending' ? ('asc' as 'asc') : ('desc' as 'desc');
  };

  const sortOptions = ['Ascending', 'Descending'].map((sort) => ({
    label: sort,
    value: sort,
    isSelected: defaultSort.direction === getSortCode({ sort }),
    onSelected: () => {
      setDefaultSort({ ...defaultSort, direction: getSortCode({ sort }) });
    },
  }));

  const slideoutWidthOptions = (['default', 'wide'] as const).map((value) => ({
    label: value === 'wide' ? 'Wide' : 'Default',
    value,
    isSelected: (slideoutWidth ?? 'default') === value,
    onSelected: () => {
      setSlideoutWidth(value);
    },
  }));

  const slideoutModeOptions = (['squish', 'overlay'] as const).map((value) => ({
    label: value === 'overlay' ? 'Overlay' : 'Squish',
    value,
    isSelected: (slideoutDisplayMode ?? 'squish') === value,
    onSelected: () => {
      setSlideoutDisplayMode(value);
    },
  }));

  const handleSave = useCallback(() => {
    updateGeneral({
      draftBoard,
    });
  }, [updateGeneral, draftBoard]);

  return (
    <BoardGeneralSettings
      board={board}
      baseView={baseView}
      name={name}
      onNameChanged={onNameChanged}
      fieldOptions={fieldOptions}
      sortOptions={sortOptions}
      plates={plates}
      setPlates={setPlates}
      hasUnsavedChanges={hasUnsavedChanges}
      handleSave={handleSave}
      isEntityFilterEnabled={isEntityFilterEnabled}
      setIsEntityFilterEnabled={setIsEntityFilterEnabled}
      isAsset={isAsset}
      setIsAsset={setIsAsset}
      labelIds={labelIds}
      setLabelIds={setLabelIds}
      slideoutWidthOptions={slideoutWidthOptions}
      slideoutModeOptions={slideoutModeOptions}
    />
  );
};

const Gate = () => {
  const { board } = useContext(BoardContext);
  if (isPerformanceBoard(board)) {
    return <BoardGeneralSettingsContainer board={board} />;
  } else {
    return null;
  }
};

export default Gate;
